import { useEffect, useState } from 'react'
import { useLocation, useParams, Link } from 'react-router-dom'
import {
  ArrowRight,
  BookOpen,
  Calendar,
  ChevronLeft,
  Download,
  FileText,
  Globe2,
  Heart,
  Library,
  ShoppingCart,
  Sparkles,
  Star,
  Tags
} from 'lucide-react'
import { publicApi } from '../services/publicApi'
import { buildAmazonUrl } from '../utils/amazon'
import SiteHeader from './SiteHeader'

const APP_STORE_URL = 'https://apps.apple.com/gb/app/bukito/id6756301072'

function extractTags(aiTags) {
  if (!aiTags) return []
  const values = Array.isArray(aiTags) ? aiTags : Object.values(aiTags).flat()
  return values
    .map((t) => (typeof t === 'string' ? t : t?.label || t?.name || t?.value))
    .filter((t) => typeof t === 'string' && t.trim())
    .slice(0, 8)
}

function cleanDescription(text) {
  if (!text) return ''
  return String(text).replace(/<[^>]+>/g, '').trim()
}

function formatLanguage(language) {
  if (!language) return ''
  const value = String(language).trim()
  if (!value) return ''
  return value.toLowerCase().startsWith('en') ? 'English' : value
}

const DetailRow = ({ label, value }) =>
  value ? (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-3 text-sm">
      <span className="text-text-secondary">{label}</span>
      <span className="max-w-[62%] text-right font-bold text-text-primary">{value}</span>
    </div>
  ) : null

const FactTile = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="rounded-lg border border-gray-100 bg-bg-surface p-4 shadow-soft">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
        <Icon size={19} />
      </div>
      <p className="text-xs font-bold uppercase text-text-secondary">{label}</p>
      <p className="mt-1 text-lg font-extrabold text-text-primary">{value}</p>
    </div>
  ) : null

const EmptyState = ({ title, children }) => (
  <div className="rounded-lg border border-gray-100 bg-bg-surface px-6 py-16 text-center shadow-soft">
    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
      <BookOpen size={24} />
    </div>
    <h1 className="text-2xl font-extrabold text-text-primary">{title}</h1>
    <div className="mt-3 text-text-secondary">{children}</div>
  </div>
)

const BookDetail = () => {
  const { id } = useParams()
  const location = useLocation()
  const [book, setBook] = useState(null)
  const [status, setStatus] = useState('loading') // loading | ready | notfound | error

  useEffect(() => {
    let active = true
    setStatus('loading')
    window.scrollTo(0, 0)
    publicApi
      .getBook(id)
      .then((data) => {
        if (!active) return
        setBook(data)
        setStatus('ready')
      })
      .catch((err) => {
        if (!active) return
        setStatus(String(err?.message).includes('404') ? 'notfound' : 'error')
      })
    return () => {
      active = false
    }
  }, [id])

  const author = book?.author || (Array.isArray(book?.authors) ? book.authors.join(', ') : '')
  const cover = book?.cover_url || book?.coverUrl || book?.thumbnail
  const rating = book?.average_rating ?? book?.averageRating
  const reviewCount = book?.total_reviews ?? book?.totalReviews
  const age = book?.reading_age || book?.age_range || book?.reading_level
  const pages = book?.number_of_pages ?? book?.pages ?? book?.numberOfPages
  const year = book?.published_year || (book?.publication_date ? String(book.publication_date).slice(0, 4) : null)
  const language = formatLanguage(book?.language)
  const tags = extractTags(book?.ai_tags_display)
  const description = cleanDescription(book?.description)
  const amazonUrl = book ? buildAmazonUrl(book) : null
  const backTo = location.state?.backTo || { pathname: '/', hash: '#new-releases' }

  return (
    <div className="min-h-screen bg-bg-main">
      <SiteHeader />

      <main className="container-custom py-8 lg:py-10">
        <Link
          to={backTo}
          className="mb-6 inline-flex items-center gap-1 rounded-lg px-1 text-sm font-bold text-primary-700 hover:text-primary-500"
        >
          <ChevronLeft size={16} />
          Back
        </Link>

        {status === 'loading' && (
          <div className="grid animate-pulse gap-8 lg:grid-cols-[300px_1fr]">
            <div className="rounded-lg bg-bg-surface p-5 shadow-soft">
              <div className="aspect-[2/3] rounded-lg bg-bg-soft" />
            </div>
            <div className="rounded-lg bg-bg-surface p-6 shadow-soft">
              <div className="mb-5 h-7 w-40 rounded bg-bg-soft" />
              <div className="mb-4 h-12 w-3/4 rounded bg-bg-soft" />
              <div className="h-5 w-1/2 rounded bg-bg-soft" />
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="h-28 rounded-lg bg-bg-soft" />
                ))}
              </div>
            </div>
          </div>
        )}

        {status === 'notfound' && (
          <EmptyState title="Book not found">
            <p>This book may no longer be available.</p>
            <Link to="/lists" className="btn-primary mt-6 inline-flex">Browse lists</Link>
          </EmptyState>
        )}

        {status === 'error' && (
          <EmptyState title="Something went wrong">
            <p>Please try again in a moment.</p>
          </EmptyState>
        )}

        {status === 'ready' && book && (
          <div className="grid gap-8 lg:grid-cols-[320px_minmax(0,1fr)]">
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-lg border border-gray-100 bg-bg-surface p-5 shadow-medium">
                <div className="rounded-lg bg-bg-soft p-4 shadow-large">
                  <div className="flex min-h-[360px] items-center justify-center rounded-lg bg-bg-soft p-3 sm:min-h-[430px]">
                    {cover ? (
                      <img
                        src={cover}
                        alt={book.title}
                        className="max-h-[560px] w-auto max-w-full rounded-lg object-contain shadow-soft"
                      />
                    ) : (
                      <div className="flex h-full min-h-[320px] w-full items-center justify-center text-primary-300">
                        <BookOpen size={48} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  {amazonUrl && (
                    <a
                      href={amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-yellow-500 px-5 text-sm font-extrabold text-text-primary shadow-soft transition-colors hover:bg-yellow-600"
                    >
                      <ShoppingCart size={18} />
                      Buy on Amazon
                    </a>
                  )}
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-5 text-sm font-extrabold text-white shadow-button transition-colors hover:bg-primary-600"
                  >
                    <Download size={18} />
                    Track in Bukito
                  </a>
                </div>
              </div>
            </aside>

            <div className="min-w-0">
              <section className="rounded-lg border border-gray-100 bg-bg-surface p-6 shadow-soft sm:p-8 lg:p-10">
                <div className="mb-5 flex flex-wrap items-center gap-2">
                  {age && (
                    <span className="inline-flex items-center gap-2 rounded-lg bg-primary-50 px-3 py-2 text-sm font-bold text-primary-700">
                      <Library size={16} />
                      Ages {age}
                    </span>
                  )}
                  {rating != null && (
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-50 px-3 py-2 text-sm font-bold text-text-primary">
                      <Star size={16} className="fill-yellow-500 text-yellow-500" />
                      {Number(rating).toFixed(1)}
                      {reviewCount != null && (
                        <span className="font-semibold text-text-secondary">
                          ({reviewCount})
                        </span>
                      )}
                    </span>
                  )}
                </div>

                <h1 className="max-w-4xl text-4xl font-extrabold leading-tight text-text-primary sm:text-5xl">
                  {book.title}
                </h1>
                {author && (
                  <p className="mt-3 text-xl font-semibold text-text-secondary">
                    by {author}
                  </p>
                )}
                {book.illustrator && (
                  <p className="mt-1 text-base text-text-secondary">
                    illustrated by {book.illustrator}
                  </p>
                )}

                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                  <FactTile icon={FileText} label="Pages" value={pages} />
                  <FactTile icon={Calendar} label="Published" value={year} />
                  <FactTile icon={Globe2} label="Language" value={language} />
                </div>
              </section>

              <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-8">
                  <section className="rounded-lg border border-gray-100 bg-bg-surface p-6 shadow-soft sm:p-8">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                        <BookOpen size={19} />
                      </span>
                      <h2 className="text-2xl font-extrabold text-text-primary">About this book</h2>
                    </div>
                    {description ? (
                      <p className="text-lg leading-relaxed text-text-primary whitespace-pre-line">
                        {description}
                      </p>
                    ) : (
                      <p className="text-lg leading-relaxed text-text-secondary">
                        Bukito is still gathering more detail for this title. Save it in the app to keep it close while the library improves.
                      </p>
                    )}
                  </section>

                  {tags.length > 0 && (
                    <section className="rounded-lg border border-gray-100 bg-bg-surface p-6 shadow-soft sm:p-8">
                      <div className="mb-4 flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-50 text-pink-700">
                          <Tags size={19} />
                        </span>
                        <h2 className="text-2xl font-extrabold text-text-primary">Reading fit</h2>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-lg bg-bg-soft px-3 py-2 text-sm font-bold text-primary-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </section>
                  )}
                </div>

                <aside className="space-y-6">
                  <section className="rounded-lg border border-gray-100 bg-primary-50 p-6 shadow-soft">
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-bg-surface text-primary-700">
                      <Sparkles size={19} />
                    </div>
                    <h2 className="text-xl font-extrabold text-text-primary">Why keep it in Bukito?</h2>
                    <ul className="mt-4 space-y-3 text-sm leading-relaxed text-text-secondary">
                      <li className="flex gap-2">
                        <Heart size={17} className="mt-0.5 shrink-0 text-pink-500" />
                        Save it to a family reading list.
                      </li>
                      <li className="flex gap-2">
                        <BookOpen size={17} className="mt-0.5 shrink-0 text-primary-500" />
                        Track when it becomes a favourite.
                      </li>
                      <li className="flex gap-2">
                        <ArrowRight size={17} className="mt-0.5 shrink-0 text-yellow-700" />
                        Find nearby recommendations faster.
                      </li>
                    </ul>
                  </section>

                  <section className="rounded-lg border border-gray-100 bg-bg-surface p-6 shadow-soft">
                    <h2 className="text-xl font-extrabold text-text-primary">Book details</h2>
                    <div className="mt-3">
                      <DetailRow label="Publisher" value={book.publisher} />
                      <DetailRow label="ISBN-13" value={book.isbn13} />
                      <DetailRow label="Reading age" value={age} />
                      <DetailRow label="Pages" value={pages} />
                      <DetailRow label="Published" value={year} />
                      <DetailRow label="Language" value={language} />
                    </div>
                  </section>
                </aside>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default BookDetail
